import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FeatureFlaggingService } from 'src/app/feature-flagging/feature-flagging.service';
import { FeatureFlags } from 'src/app/feature-flagging/feature-flags';
import { throwError } from 'rxjs';
import { ActivitiesResponseList, ActivityTypesResponse, IATypes } from 'src/app/models/interactive-activity/interactive-activity.model';
import { ListParams } from 'src/app/models/list-params/list-params.model';
import { AuthService } from 'src/app/services/auth.service';
import { DialogService } from 'src/app/services/dialog.service';
import { InteractiveActivityService } from 'src/app/services/interactive-activity.service';
import { InteractiveActivityTypesService } from 'src/app/services/interactive-activity-types.service';
import { formatError } from 'src/app/utils/errorMessage.formatter';
import { HeaderComponent } from 'src/app/shared/header/header.component';
import { ListPaginatorModule } from 'src/app/shared/list/list-paginator/list-paginator.module';
import { ListToolbarModule } from 'src/app/shared/list/list-toolbar/list-toolbar.module';
import { ListViewModule } from 'src/app/shared/list/list-view/list-view.module';
import { LoaderComponent } from 'src/app/shared/loader/loader.component';
import { SpinnerModule } from 'src/app/shared/spinner/spinner.module';
import { TextfieldComponent } from 'src/app/shared/form/textfield/textfield.component';
import { InteractiveActivityTypesComponent } from '../IA-types/interactive-activity-types.component';

@Component({
  selector: 'app-interactive-activities-list',
  templateUrl: './interactive-activities-list.component.html',
  styleUrls: ['./interactive-activities-list.component.scss'],
  imports: [
    CommonModule,
    HeaderComponent,
    InteractiveActivityTypesComponent,
    ListPaginatorModule,
    ListToolbarModule,
    ListViewModule,
    LoaderComponent,
    RouterModule,
    SpinnerModule,
    TextfieldComponent,
  ],
})
export class InteractiveActivitiesListComponent implements OnInit {
  private readonly dialogService = inject(DialogService);
  private readonly iaService = inject(InteractiveActivityService);
  private readonly iaTypesService = inject(InteractiveActivityTypesService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly featureFlaggingService = inject(FeatureFlaggingService);

  newActivityOptions: Array<{ label: string; url: string }> = [];
  newIaTypeOptions: Array<{ label: string; url: string }> = [{ label: 'Custom HTML Type', url: '/interactive-activity-type/new' }];
  get canManageIaTypes(): boolean {
    return this.featureFlaggingService.isFeatureEnabled(FeatureFlags.CAN_CREATE_EDIT_INTERACTIVE_ACTIVITY_TYPES);
  }

  headers = [
    { label: 'Title', key: 'title', sortable: true },
    { label: 'Activity Type', key: 'interactiveActivityTypeTitle', sortable: true },
    { label: 'Date Edited', key: 'updatedAt', sortable: true },
    { label: 'Date Created', key: 'createdAt', sortable: true },
    { label: 'Linked Content', key: 'contentTitle', sortable: true },
    { label: 'Actions' },
  ];

  typeOptions: IATypes[] = [];
  loading = false;
  sorting = false;
  searchTerm = '';
  sortingParams?: { key?: string; asc?: boolean };
  currentPage = 1;
  deletingContentId: string = '0';
  activitiesList?: ActivitiesResponseList;
  iaTypes: { [id: number]: string } = {};
  activitiesFilter?: IATypes[];
  activeTab: number = 0;
  allIaTypes = [];

  ngOnInit(): void {
    this.loadActivityTypes();
  }

  // Fetches all AI types dynamically.
  loadActivityTypes(): void {
    this.iaTypesService.list().subscribe(
      (response: ActivityTypesResponse) => {
        this.newActivityOptions = response.interactiveActivityTypes.map(iaType => ({
          label: iaType.title,
          url: `/interactive-activities/${iaType.slug}/new`,
        }));

        this.typeOptions = [
          { id: 1, label: 'All' },
          ...response.interactiveActivityTypes.map((iaType, index) => ({ id: index + 2, label: iaType.title })),
        ];

        this.iaTypes = {};
        response.interactiveActivityTypes.forEach((iaType, index) => (this.iaTypes[index + 2] = iaType.slug));
        Object.values(this.iaTypes).forEach(slug => this.allIaTypes.push(slug));

        this.loadActivitiesList();
      },
      () => {
        this.loadActivitiesList();
      }
    );
  }

  loadActivitiesList(hideLoader?: boolean) {
    var params: ListParams = { page: this.currentPage };
    if (this.searchTerm) {
      params.text = this.searchTerm;
    }

    if (this.sortingParams) {
      params.orderBy = this.sortingParams.key;
      params.sort = this.sortingParams.asc ? 'asc' : 'desc';
    }
    if (this.activitiesFilter && this.activitiesFilter.length > 0) {
      params['interactiveActivityTypes'] = this.activitiesFilter.map(m => m);
    } else {
      params['interactiveActivityTypes'] = this.allIaTypes;
    }

    if (!hideLoader) {
      this.loading = true;
    }

    this.iaService.getList(params).subscribe(
      (response: ActivitiesResponseList) => {
        response.interactiveActivities.forEach(activity => {
          activity.createdAt = new Date(activity.createdAt).toLocaleDateString('en-US');
          activity.updatedAt = new Date(activity.updatedAt).toLocaleDateString('en-US');
        });
        this.activitiesList = response;
        this.sorting = false;
        this.loading = false;
      },
      error => {
        this.sorting = false;
        this.loading = false;
        this.dialogService.showErrorAlert(formatError(error));
        if (error.status === 401) {
          this.authService.clearSession();
          this.router.navigate(['/login']);
        }
      }
    );
  }

  onSearchChange(term: string) {
    this.searchTerm = term;
    this.currentPage = 1;
    this.loadActivitiesList();
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.loadActivitiesList();
  }

  onSortChange(params: { key: string; asc: boolean }) {
    this.sorting = true;
    this.sortingParams = params;
    this.loadActivitiesList(true);
  }

  onTypeChange(types: IATypes[]) {
    let translatedType = [];

    types.forEach(type => {
      if (type.label.toLowerCase() !== 'all') {
        translatedType.push(this.iaTypes[type.id]);
      }
    });

    this.activitiesFilter = translatedType;
    this.loadActivitiesList();
  }

  confirmDelete(activityId: string, activityTitle: string) {
    this.dialogService.showConfirmationDialog({
      type: 'danger',
      confirmButtonLabel: 'Delete',
      onConfirmCallback: () => this.delete(activityId),
      dialogIconUrl: '../../../../assets/images/icons/alert-circle.svg',
      confirmButtonIconUrl: '../../../../assets/images/icons/delete.svg',
      title: 'Are you sure you want to remove ' + activityTitle + ' activity from the activities?',
      message: 'This action cannot be undone.',
    });
  }

  delete(activityId) {
    this.deletingContentId = activityId;
    this.iaService.delete(activityId).subscribe(
      () => {
        this.deletingContentId = null;
        this.dialogService.showSuccessAlert('Activity has been deleted.');
        this.resetVariables();
        this.loadActivitiesList();
      },
      error => {
        this.dialogService.showInformativeModal({ typeModal: 'errorDialog', props: { message: 'Error on delete IA, please contact the support.' } });
        throwError(error);
      }
    );
  }

  resetVariables(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.sorting = false;
  }
}
